import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import config from '../../config';

export function MfaVerificationPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [mfaCode, setMfaCode] = useState('');
    const [error, setError] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [useBackupCode, setUseBackupCode] = useState(false);

    // Get user data from location state (passed from login page)
    const userData = location.state?.userData;

    useEffect(() => {
        // Redirect to login if no user data
        if (!userData || !userData.mfaRequired) {
            navigate('/login');
        }
    }, [userData, navigate]);

    if (!userData) {
        return null;
    }

    function handleSubmit(e) {
        e.preventDefault();

        if (!mfaCode) {
            setError('Please enter the verification code');
            return;
        }

        if (!useBackupCode && mfaCode.length !== 6) {
            setError('Verification code must be 6 digits');
            return;
        }

        if (useBackupCode && mfaCode.length !== 8) {
            setError('Backup code must be 8 characters');
            return;
        }

        setVerifying(true);
        setError('');

        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userId: userData.id,
                mfaCode: mfaCode
            })
        };

        fetch(`${config.apiUrl}/api/users/verify-mfa`, requestOptions)
            .then(handleResponse)
            .then(
                user => {
                    setVerifying(false);
                    sessionStorage.setItem('user', JSON.stringify(user));
                    window.dispatchEvent(new Event('storage'));

                    // Navigate based on user's connection status
                    let destinationUri = '/profile';

                    if (user.connections) {
                        const sources = new Set(user.connections.split(","));
                        if (sources.has('source') && sources.has('target')) destinationUri = '/profile/dashboard';
                        else if (sources.has('source')) destinationUri = '/profile/configureSource';
                        else if (sources.has('target')) destinationUri = '/profile/configureTarget';
                    }

                    navigate(destinationUri);
                },
                error => {
                    setError(error);
                    setVerifying(false);
                }
            );
    }

    function handleResponse(response) {
        return response.text().then(text => {
            const data = text && JSON.parse(text);
            if (!response.ok) {
                const error = (data && data.message) || response.statusText;
                return Promise.reject(error);
            }
            return data;
        });
    }

    return (
        <div className="min-vh-100 d-flex align-items-center" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-md-6 col-lg-4">
                        <div className="modern-card text-center mb-4">
                            <div className="card-body p-5">
                                <div className="mb-4">
                                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                         style={{width: '4rem', height: '4rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
                                        <i className="bi bi-shield-lock-fill text-white fs-3"></i>
                                    </div>
                                    <h2 className="fw-bold mb-2" style={{color: '#1e293b'}}>Two-Factor Authentication</h2>
                                    <p className="text-muted mb-0">
                                        Enter the {useBackupCode ? 'backup code' : '6-digit code'} from your authenticator app
                                    </p>
                                </div>

                                {error && (
                                    <div className="alert alert-danger rounded-3 mb-4" role="alert">
                                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                        {error}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit}>
                                    <div className="mb-4">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className="form-control text-center"
                                                id="mfaCode"
                                                name="mfaCode"
                                                value={mfaCode}
                                                onChange={(e) => setMfaCode(e.target.value)}
                                                placeholder={useBackupCode ? "Enter backup code" : "000000"}
                                                maxLength={useBackupCode ? 8 : 6}
                                                style={{fontSize: '1.5rem', letterSpacing: '0.5rem'}}
                                                autoComplete="off"
                                                autoFocus
                                            />
                                            <label htmlFor="mfaCode">
                                                {useBackupCode ? 'Backup Code' : 'Verification Code'}
                                            </label>
                                        </div>
                                    </div>

                                    <div className="d-grid gap-2 mb-4">
                                        <button
                                            type="submit"
                                            className="btn btn-modern btn-lg"
                                            disabled={verifying}
                                        >
                                            {verifying ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Verifying...
                                                </>
                                            ) : (
                                                'Verify'
                                            )}
                                        </button>
                                    </div>

                                    <div className="text-center">
                                        <button
                                            type="button"
                                            className="btn btn-link text-muted"
                                            onClick={() => {
                                                setUseBackupCode(!useBackupCode);
                                                setMfaCode('');
                                                setError('');
                                            }}
                                        >
                                            {useBackupCode ? 'Use authenticator code' : 'Use backup code instead'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        <div className="text-center">
                            <p className="text-white-50 small mb-0">
                                <i className="bi bi-info-circle me-1"></i>
                                Having trouble? Contact support for assistance
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
